////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. FILE HEADER
// BitsBound Documentation Page - Comprehensive customer-facing documentation
// Created: 2025-12-08
// Purpose: Explain how BitsBound works for customers

// II. IMPORTS
import React, { useState, useEffect } from 'react';

// III. TYPES
interface DocumentationPeaProps {
    className?: string;
    initialSection?: string; // For deep-linking to specific sections (e.g., /docs/mcp -> 'mcp')
}

interface DocSection {
    id: string;
    title: string;
    icon: string;
    content: React.ReactNode;
}

// IV. STYLES (inline for dark theme consistency - matching Privacy/Security/Terms pages)
const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: '#ffffff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    header: {
        padding: '24px 48px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(180deg, rgba(32,178,170,0.08) 0%, transparent 100%)',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    logoLink: {
        display: 'flex',
        alignItems: 'center',
    },
    logo: {
        height: '40px',
    },
    headerTitleWrapper: {},
    headerTitle: {
        fontSize: '24px',
        fontWeight: 600,
        margin: 0,
        color: '#20b2aa',
    },
    headerSubtitle: {
        fontSize: '12px',
        color: 'rgba(255,255,255,0.5)',
        margin: '4px 0 0 0',
    },
    main: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '40px',
        display: 'flex',
        gap: '48px',
    },
    sidebar: {
        width: '300px',
        flexShrink: 0,
    },
    sidebarInner: {
        position: 'sticky' as const,
        top: '32px',
        backgroundColor: 'rgba(255,255,255,0.02)',
        borderRadius: '20px',
        padding: '20px',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        backdropFilter: 'blur(10px)',
    },
    sidebarHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    sidebarIcon: {
        fontSize: '20px',
    },
    navTitle: {
        fontSize: '14px',
        textTransform: 'uppercase' as const,
        letterSpacing: '1.5px',
        color: 'rgba(255,255,255,0.6)',
        margin: 0,
        fontWeight: 600,
    },
    navList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '4px',
    },
    navItem: {
        width: '100%',
        padding: '12px 14px',
        textAlign: 'left' as const,
        backgroundColor: 'transparent',
        border: '1px solid transparent',
        borderRadius: '12px',
        color: 'rgba(255,255,255,0.7)',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 400,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        transition: 'all 0.2s ease',
    },
    navItemActive: {
        backgroundColor: 'rgba(32, 178, 170, 0.15)',
        border: '1px solid rgba(32, 178, 170, 0.3)',
        color: '#20b2aa',
        fontWeight: 600,
    },
    navItemIcon: {
        fontSize: '16px',
        width: '24px',
        textAlign: 'center' as const,
    },
    navItemLabel: {
        flex: 1,
    },
    navItemIndicator: {
        color: '#20b2aa',
        fontSize: '12px',
    },
    content: {
        flex: 1,
        minWidth: 0,
    },
    contentCard: {
        backgroundColor: 'rgba(255,255,255,0.02)',
        borderRadius: '20px',
        padding: '40px',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        backdropFilter: 'blur(10px)',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '1px solid rgba(32,178,170,0.2)',
    },
    sectionIconWrapper: {
        width: '56px',
        height: '56px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, rgba(32,178,170,0.2) 0%, rgba(32,178,170,0.05) 100%)',
        border: '1px solid rgba(32,178,170,0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '28px',
    },
    sectionTitle: {
        fontSize: '28px',
        fontWeight: 700,
        margin: 0,
        color: '#ffffff',
    },
    sectionSubtitle: {
        fontSize: '14px',
        color: 'rgba(255,255,255,0.5)',
        margin: '4px 0 0 0',
    },
    paragraph: {
        color: 'rgba(255,255,255,0.85)',
        lineHeight: 1.8,
        marginBottom: '1.5rem',
        fontSize: '15px',
    },
    highlight: {
        background: 'rgba(32, 178, 170, 0.1)',
        borderLeft: '4px solid #20b2aa',
        padding: '1rem 1.5rem',
        borderRadius: '0 10px 10px 0',
        marginBottom: '1.5rem',
    },
    highlightTitle: {
        color: '#20b2aa',
        fontWeight: 600,
        marginBottom: '0.5rem',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
        marginBottom: '1.5rem',
    },
    card: {
        background: 'rgba(255,255,255,0.02)',
        borderRadius: '12px',
        padding: '1.5rem',
        border: '1px solid rgba(255,255,255,0.05)',
    },
    cardIcon: {
        fontSize: '2rem',
        marginBottom: '1rem',
    },
    cardTitle: {
        fontSize: '1.1rem',
        fontWeight: 600,
        marginBottom: '0.5rem',
        color: '#ffffff',
    },
    cardText: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: '0.9rem',
        lineHeight: 1.6,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse' as const,
        marginBottom: '1.5rem',
    },
    th: {
        textAlign: 'left' as const,
        padding: '1rem',
        borderBottom: '2px solid rgba(32, 178, 170, 0.3)',
        color: '#20b2aa',
        fontWeight: 600,
    },
    td: {
        padding: '1rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        color: 'rgba(255,255,255,0.85)',
    },
    footer: {
        padding: '40px 48px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        textAlign: 'center' as const,
        background: 'linear-gradient(0deg, rgba(32,178,170,0.05) 0%, transparent 100%)',
    },
    footerLinks: {
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        marginBottom: '20px',
    },
    footerLink: {
        color: 'rgba(255,255,255,0.6)',
        textDecoration: 'none',
        fontSize: '14px',
    },
    footerDivider: {
        color: 'rgba(255,255,255,0.3)',
    },
    footerCopyright: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: '14px',
        margin: 0,
    },
    footerDate: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: '12px',
        marginTop: '8px',
    },
    disclaimer: {
        background: 'rgba(255, 193, 7, 0.1)',
        border: '1px solid rgba(255, 193, 7, 0.3)',
        borderRadius: '12px',
        padding: '1rem 1.5rem',
        marginTop: '2rem',
        color: '#ffc107',
        fontSize: '0.85rem',
    },
    subheading: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#20b2aa',
        marginTop: '2rem',
        marginBottom: '1rem',
    },
    codeBlock: {
        background: 'rgba(0, 0, 0, 0.4)',
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '1.5rem',
        border: '1px solid rgba(255,255,255,0.1)',
        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
        fontSize: '0.9rem',
        color: '#20b2aa',
        overflow: 'auto',
    },
    list: {
        color: 'rgba(255,255,255,0.85)',
        lineHeight: 1.8,
        marginBottom: '1.5rem',
        paddingLeft: '1.5rem',
    },
    listItem: {
        marginBottom: '0.5rem',
    },
};

// V. COMPONENT
const DocumentationPea: React.FC<DocumentationPeaProps> = ({ className, initialSection }) => {
    // Use initialSection if provided and valid, otherwise default to 'overview'
    const [activeSection, setActiveSection] = useState(initialSection || 'overview');

    // T_MOBILE_RESPONSIVE_2026_01_02: Mobile responsiveness
    const [isMobile, setIsMobile] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // VI. DOCUMENTATION SECTIONS
    const sections: DocSection[] = [
        {
            id: 'overview',
            title: 'Overview',
            icon: '📋',
            content: (
                <>
                    <p style={styles.paragraph}>
                        BitsBound is an AI-powered contract analysis and drafting acceleration tool designed for legal professionals.
                        Upload any B2B contract, and BitsBound accelerates your first draft from days to approximately one hour.
                    </p>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>Three Draft Deliverables Per Contract</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            1. <strong>First-Draft Redlined Word Document</strong> — Real OOXML Track Changes (w:ins, w:del), ready for attorney review<br />
                            2. <strong>Draft Negotiation Email</strong> — Professional starting point explaining each requested change<br />
                            3. <strong>Analytical Dashboard</strong> — Risk heatmaps, favorability scores, section-by-section breakdown
                        </p>
                    </div>
                    <p style={styles.paragraph}>
                        BitsBound is designed as an "attorney's instrument" — sophisticated enough to accelerate the first 80% of contract review,
                        while ensuring attorneys provide the judgment and final review that matters.
                    </p>
                    <div style={styles.disclaimer}>
                        <strong>Important:</strong> BitsBound provides AI-assisted contract analysis tools for legal professionals.
                        All outputs are preliminary drafts requiring attorney review and do not constitute legal advice.
                    </div>
                </>
            ),
        },
        {
            id: 'pipeline',
            title: 'Analysis Pipeline',
            icon: '🔄',
            content: (
                <>
                    <p style={styles.paragraph}>
                        BitsBound processes contracts through a sophisticated seven-phase progressive pipeline,
                        combining deterministic extraction with parallel AI analysis for comprehensive coverage.
                    </p>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Phase</th>
                                <th style={styles.th}>Description</th>
                                <th style={styles.th}>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td style={styles.td}><strong>1. Context Loading</strong></td><td style={styles.td}>Load deal context, industry, and position</td><td style={styles.td}>~0ms</td></tr>
                            <tr><td style={styles.td}><strong>2. Deterministic Extraction</strong></td><td style={styles.td}>12 parallel regex scripts extract structured data</td><td style={styles.td}>~50ms</td></tr>
                            <tr><td style={styles.td}><strong>3. Parties Identification</strong></td><td style={styles.td}>AI identifies parties to enable targeted research</td><td style={styles.td}>~30s</td></tr>
                            <tr><td style={styles.td}><strong>4. Engagement Research</strong></td><td style={styles.td}>6-domain pre-analysis intelligence gathering</td><td style={styles.td}>~30s</td></tr>
                            <tr><td style={styles.td}><strong>5. Full AI Analysis</strong></td><td style={styles.td}>16 parallel AI analyzers examine every domain</td><td style={styles.td}>~90s</td></tr>
                            <tr><td style={styles.td}><strong>6. AI Synthesis</strong></td><td style={styles.td}>Cross-section synthesis and strategy development</td><td style={styles.td}>~30s</td></tr>
                            <tr><td style={styles.td}><strong>7. SAC + Redlines</strong></td><td style={styles.td}>Interactive review with Supreme AI Co-Counsel</td><td style={styles.td}>User-driven</td></tr>
                        </tbody>
                    </table>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>Total Processing Time</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            Phases 1-6 complete in approximately 3-4 minutes. Phase 7 is interactive — you control the pace.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: 'three-brains',
            title: 'Three-Brain Architecture',
            icon: '🧠',
            content: (
                <>
                    <p style={styles.paragraph}>
                        BitsBound's core innovation is the Three-Brain Pipeline — a multi-stage AI architecture
                        that combines comprehensive analysis with playbook enforcement.
                    </p>
                    <div style={styles.grid}>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🐝</div>
                            <div style={styles.cardTitle}>Brain 1: The Swarm</div>
                            <div style={styles.cardText}>
                                16 specialized AI analyzers run in parallel, each examining a specific domain:
                                parties, definitions, IP, SLA, DPA, commercial terms, liability, indemnification,
                                warranties, economic exposure, and more. Complete coverage in minutes.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>⚖️</div>
                            <div style={styles.cardTitle}>Brain 2: SAC (Analysis Context)</div>
                            <div style={styles.cardText}>
                                Supreme AI Co-Counsel has full context from all 16 analyzers plus the complete contract text.
                                Uses a 1M token context window to understand cross-section implications and draft precise redlines.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>📚</div>
                            <div style={styles.cardTitle}>Brain 3: Autopilot (Playbook Rules)</div>
                            <div style={styles.cardText}>
                                Enforces YOUR firm's playbook rules and pre-approved clause library.
                                You can upload custom playbooks. SAC and Autopilot iterate until consensus on each redline.
                            </div>
                        </div>
                    </div>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>The Ping-Pong Process</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            SAC drafts a redline based on analysis. Autopilot checks it against your playbook.
                            They volley back and forth sentence-by-sentence until they agree — giving you a solid starting point.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: 'research',
            title: 'Pre-Analysis Research',
            icon: '🔍',
            content: (
                <>
                    <p style={styles.paragraph}>
                        Before any analysis runs, BitsBound executes parallel research across six intelligence domains.
                        This enriches every downstream analyzer with context that goes beyond the four corners of the contract.
                    </p>
                    <div style={styles.grid}>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🏢</div>
                            <div style={styles.cardTitle}>Counterparty Due Diligence</div>
                            <div style={styles.cardText}>
                                Corporate structure, ownership, financial stability, M&A history, and litigation patterns.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>📜</div>
                            <div style={styles.cardTitle}>Regulatory Landscape</div>
                            <div style={styles.cardText}>
                                GDPR, CCPA, HIPAA applicability. Recent enforcement actions. Compliance requirements.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>📊</div>
                            <div style={styles.cardTitle}>Industry Benchmarks</div>
                            <div style={styles.cardText}>
                                Market-standard terms for your deal size and type. What's typical vs. aggressive.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>⚖️</div>
                            <div style={styles.cardTitle}>Clause Jurisprudence</div>
                            <div style={styles.cardText}>
                                How courts interpret specific provisions. Recent case law. Enforceability patterns.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>📈</div>
                            <div style={styles.cardTitle}>Market Intelligence</div>
                            <div style={styles.cardText}>
                                Counterparty's public commitments (privacy policy, SLA, security page).
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>⚠️</div>
                            <div style={styles.cardTitle}>Risk Signals</div>
                            <div style={styles.cardText}>
                                Data breach history, BBB complaints, consent decrees, cybersecurity posture.
                            </div>
                        </div>
                    </div>
                </>
            ),
        },
        {
            id: 'bubsa',
            title: 'BUBSA Framework',
            icon: '📑',
            content: (
                <>
                    <p style={styles.paragraph}>
                        BUBSA (BitsBound Universal Baseline Standardized Agreement) is our comprehensive 24-section framework
                        for analyzing <strong>any B2B contract</strong>. Whether you're reviewing SaaS agreements, M&A transactions,
                        equipment financing, commercial leases, NDAs, or professional services agreements, BUBSA provides the
                        analytical benchmark for contract completeness and favorability scoring.
                    </p>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>Essential Sections (6)</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            Parties • Incorporated Agreements • Definitions • IP License Grant • Payment • Term/Termination
                        </p>
                    </div>
                    <div style={{ ...styles.highlight, borderLeftColor: '#ffc107' }}>
                        <div style={{ ...styles.highlightTitle, color: '#ffc107' }}>Legal Sections (9)</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            Insurance • Confidentiality • Privacy • Reps & Warranties • Indemnification •
                            Limitation of Liability • Assignment • Force Majeure • ADR/Governing Law
                        </p>
                    </div>
                    <div style={{ ...styles.highlight, borderLeftColor: '#9c27b0' }}>
                        <div style={{ ...styles.highlightTitle, color: '#9c27b0' }}>Operational Sections (7)</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            Order of Precedence • Severability • Entire Agreement • Amendments • Survival • Notices • Execution
                        </p>
                    </div>
                    <div style={{ ...styles.highlight, borderLeftColor: '#2196f3' }}>
                        <div style={{ ...styles.highlightTitle, color: '#2196f3' }}>Supplementary Sections (2)</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            Service Level Agreement (SLA) • Data Processing Addendum (DPA)
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: 'redlines',
            title: 'Track Changes',
            icon: '📝',
            content: (
                <>
                    <p style={styles.paragraph}>
                        BitsBound doesn't create visual approximations of redlines — we perform actual OOXML surgery
                        on your original .docx file, producing real Track Changes that work in Word's Review pane.
                    </p>
                    <div style={styles.grid}>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>✂️</div>
                            <div style={styles.cardTitle}>Surgical Precision</div>
                            <div style={styles.cardText}>
                                We use Claude Opus 4.5 for OOXML surgery, handling Word's complex character-level XML
                                splitting and maintaining all original formatting.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🩸</div>
                            <div style={styles.cardTitle}>Minimal Blood Principle</div>
                            <div style={styles.cardText}>
                                We use the Myers O(ND) diff algorithm (same as Git) to compute the shortest edit script.
                                If a redline would create a "confetti effect," we reject and recompute.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>💬</div>
                            <div style={styles.cardTitle}>Margin Comments</div>
                            <div style={styles.cardText}>
                                Every redline includes a margin comment (w:comment) explaining the rationale,
                                with BUBSA section references for context.
                            </div>
                        </div>
                    </div>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>What You Get</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            Real &lt;w:ins&gt; and &lt;w:del&gt; elements. Real RSIDs and timestamps.
                            Configurable author attribution. Opens in Word's Review pane exactly like attorney redlines.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: 'playbooks',
            title: 'Custom Playbooks',
            icon: '📚',
            content: (
                <>
                    <p style={styles.paragraph}>
                        Upload your firm's playbook documents and BitsBound will enforce your specific policies,
                        pre-approved language, risk tolerances, and counterparty-specific rules during the redlining process.
                    </p>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>What Playbooks Contain</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            • <strong>Red flags</strong> — Patterns that trigger immediate attention<br />
                            • <strong>Standard positions</strong> — Your default negotiating stance<br />
                            • <strong>Fallback positions</strong> — Acceptable compromises<br />
                            • <strong>Pre-approved clause language</strong> — Ready-to-use alternatives<br />
                            • <strong>Walk-away lines</strong> — Terms you won't accept
                        </p>
                    </div>
                    <p style={styles.paragraph}>
                        The playbook system learns over time. When attorneys accept or reject BitsBound's suggestions,
                        we use Thompson Sampling to improve future recommendations — getting smarter with every contract.
                    </p>
                </>
            ),
        },
        {
            id: 'sac',
            title: 'Supreme AI Co-Counsel',
            icon: '💬',
            content: (
                <>
                    <p style={styles.paragraph}>
                        SAC is your interactive interface for discussing the contract, asking questions,
                        and directing redline generation. It has full context from all analysis phases.
                    </p>
                    <div style={styles.grid}>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🎯</div>
                            <div style={styles.cardTitle}>Full Context Awareness</div>
                            <div style={styles.cardText}>
                                SAC receives the complete contract text plus outputs from all 16 analyzers.
                                It understands cross-section implications and can quote exact clause language.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🔄</div>
                            <div style={styles.cardTitle}>Dynamic Context Loading</div>
                            <div style={styles.cardText}>
                                When you discuss SLA issues, SAC loads SLA-specific analysis.
                                Discussing IP? It loads IP license analysis. Always relevant context.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>⚡</div>
                            <div style={styles.cardTitle}>Streaming Responses</div>
                            <div style={styles.cardText}>
                                Real-time token streaming so you see SAC's thinking as it develops.
                                No waiting for complete responses.
                            </div>
                        </div>
                    </div>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>What You Can Ask SAC</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            "What's the liability exposure here?" • "Draft a redline for Section 8.2" •
                            "What's wrong with this indemnification clause?" • "Generate alternatives for the IP license"
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: 'pricing',
            title: 'Pricing & Economics',
            icon: '💰',
            content: (
                <>
                    <p style={styles.paragraph}>
                        BitsBound is priced to deliver massive value while maintaining healthy margins.
                        Compare our cost to traditional attorney review:
                    </p>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Method</th>
                                <th style={styles.th}>Time</th>
                                <th style={styles.th}>Cost</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td style={styles.td}><strong>Traditional Attorney Review</strong></td><td style={styles.td}>5-10 days</td><td style={styles.td}>$20,000 - $64,000+</td></tr>
                            <tr><td style={styles.td}><strong>BitsBound + Attorney Review</strong></td><td style={styles.td}>~1 hour + review</td><td style={styles.td}>$250 - $500 + attorney time</td></tr>
                        </tbody>
                    </table>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>Value Proposition</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            BitsBound accelerates the first 80% of contract review. Attorneys provide the judgment that matters.
                            At $300/contract, we capture less than 2% of the value we create.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: 'security',
            title: 'Security & Privacy',
            icon: '🔒',
            content: (
                <>
                    <p style={styles.paragraph}>
                        BitsBound is built with enterprise security requirements in mind.
                        Your contracts are sensitive — we treat them accordingly.
                    </p>
                    <div style={styles.grid}>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>☁️</div>
                            <div style={styles.cardTitle}>Cloud-Native Architecture</div>
                            <div style={styles.cardText}>
                                MongoDB Atlas for data, Cloudflare R2 for documents.
                                No local file storage. SOC 2 compliant infrastructure.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🔐</div>
                            <div style={styles.cardTitle}>Encryption</div>
                            <div style={styles.cardText}>
                                TLS 1.3 in transit. AES-256 at rest.
                                Your documents are encrypted throughout their lifecycle.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🗑️</div>
                            <div style={styles.cardTitle}>Data Retention</div>
                            <div style={styles.cardText}>
                                Configurable retention policies. Request deletion at any time.
                                We don't train models on your data.
                            </div>
                        </div>
                    </div>
                    <p style={styles.paragraph}>
                        Questions about security? Contact us at <strong>admin@bitsbound.com</strong>.
                    </p>
                </>
            ),
        },
        {
            id: 'rest-api',
            title: 'REST API',
            icon: '🌐',
            content: (
                <>
                    <p style={styles.paragraph}>
                        BitsBound provides a production-ready REST API for programmatic contract analysis.
                        Integrate BitsBound's contract intelligence directly into your applications, workflows,
                        or custom tools.
                    </p>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>Base URL</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            <code style={{ color: '#20b2aa' }}>https://bitsbound-api-backend.vercel.app/v1</code>
                        </p>
                    </div>

                    <h3 style={styles.subheading}>Authentication</h3>
                    <p style={styles.paragraph}>
                        All protected endpoints require an API key passed in the <code style={{ color: '#20b2aa' }}>x-api-key</code> header.
                        Generate your API key from your <a href="/account" style={{ color: '#20b2aa' }}>account dashboard</a>.
                    </p>
                    <div style={styles.codeBlock}>
                        <code style={{ whiteSpace: 'pre-wrap' }}>{`curl -H "x-api-key: sk_live_your_api_key_here" \\
  https://bitsbound-api-backend.vercel.app/v1/playbooks`}</code>
                    </div>

                    <h3 style={styles.subheading}>Available Endpoints</h3>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Endpoint</th>
                                <th style={styles.th}>Method</th>
                                <th style={styles.th}>Auth</th>
                                <th style={styles.th}>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td style={styles.td}><code>/v1/health</code></td><td style={styles.td}>GET</td><td style={styles.td}>No</td><td style={styles.td}>API health check</td></tr>
                            <tr><td style={styles.td}><code>/v1/openapi.json</code></td><td style={styles.td}>GET</td><td style={styles.td}>No</td><td style={styles.td}>OpenAPI specification</td></tr>
                            <tr><td style={styles.td}><code>/v1/playbooks</code></td><td style={styles.td}>GET</td><td style={styles.td}>Yes</td><td style={styles.td}>List your playbooks</td></tr>
                            <tr><td style={styles.td}><code>/v1/contracts/process</code></td><td style={styles.td}>POST</td><td style={styles.td}>Yes</td><td style={styles.td}>Start contract analysis</td></tr>
                        </tbody>
                    </table>

                    <h3 style={styles.subheading}>Quick Start Examples</h3>
                    <p style={styles.paragraph}>
                        <strong>Check API health:</strong>
                    </p>
                    <div style={styles.codeBlock}>
                        <code style={{ whiteSpace: 'pre-wrap' }}>{`curl https://bitsbound-api-backend.vercel.app/v1/health`}</code>
                    </div>

                    <p style={styles.paragraph}>
                        <strong>List your playbooks:</strong>
                    </p>
                    <div style={styles.codeBlock}>
                        <code style={{ whiteSpace: 'pre-wrap' }}>{`curl -H "x-api-key: sk_live_your_key" \\
  https://bitsbound-api-backend.vercel.app/v1/playbooks`}</code>
                    </div>

                    <p style={styles.paragraph}>
                        <strong>Start contract analysis:</strong>
                    </p>
                    <div style={styles.codeBlock}>
                        <code style={{ whiteSpace: 'pre-wrap' }}>{`curl -X POST \\
  -H "x-api-key: sk_live_your_key" \\
  -H "Content-Type: application/json" \\
  -d '{"contractId": "your-contract-id", "options": {...}}' \\
  https://bitsbound-api-backend.vercel.app/v1/contracts/process`}</code>
                    </div>

                    <div style={styles.grid}>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>📖</div>
                            <div style={styles.cardTitle}>OpenAPI Spec</div>
                            <div style={styles.cardText}>
                                Full API documentation available at <code>/v1/openapi.json</code>. Import into Postman or your favorite API client.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🔐</div>
                            <div style={styles.cardTitle}>Secure by Default</div>
                            <div style={styles.cardText}>
                                API keys are scoped to your account. All requests use HTTPS. Rate limiting protects against abuse.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>💰</div>
                            <div style={styles.cardTitle}>Token-Based Billing</div>
                            <div style={styles.cardText}>
                                API usage deducts from your account token balance. Same pricing as web interface.
                            </div>
                        </div>
                    </div>

                    <div style={styles.disclaimer}>
                        <strong>API Key Required:</strong> Generate your API key from your <a href="/account" style={{ color: '#20b2aa' }}>account dashboard</a> to start using the REST API.
                    </div>
                </>
            ),
        },
        {
            id: 'mcp',
            title: 'MCP Server',
            icon: '🔌',
            content: (
                <>
                    <p style={styles.paragraph}>
                        BitsBound provides a Model Context Protocol (MCP) server that enables AI assistants
                        like Claude Desktop to directly analyze contracts and generate redlines through natural conversation.
                    </p>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>What is MCP?</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            MCP (Model Context Protocol) is an open standard that allows AI assistants to connect to external tools and data sources.
                            With BitsBound's MCP server, you can analyze contracts directly from Claude Desktop without leaving the conversation.
                        </p>
                    </div>

                    <h3 style={styles.subheading}>Installation</h3>
                    <p style={styles.paragraph}>
                        Install the BitsBound MCP server from npm:
                    </p>
                    <div style={styles.codeBlock}>
                        <code>npm install -g @bitsbound/mcp-server</code>
                    </div>

                    <h3 style={styles.subheading}>Claude Desktop Configuration</h3>
                    <p style={styles.paragraph}>
                        Add the following to your Claude Desktop configuration file:
                    </p>
                    <ul style={styles.list}>
                        <li style={styles.listItem}><strong>macOS:</strong> ~/Library/Application Support/Claude/claude_desktop_config.json</li>
                        <li style={styles.listItem}><strong>Windows:</strong> %APPDATA%\Claude\claude_desktop_config.json</li>
                    </ul>
                    <div style={styles.codeBlock}>
                        <code style={{ whiteSpace: 'pre-wrap' }}>{`{
  "mcpServers": {
    "bitsbound": {
      "command": "npx",
      "args": ["-y", "@bitsbound/mcp-server"],
      "env": {
        "BITSBOUND_API_KEY": "your-api-key-here"
      }
    }
  }
}`}</code>
                    </div>

                    <h3 style={styles.subheading}>Available Commands</h3>
                    <p style={styles.paragraph}>
                        Once configured, Claude Desktop can execute these BitsBound operations:
                    </p>
                    <div style={styles.grid}>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>📤</div>
                            <div style={styles.cardTitle}>upload_contract</div>
                            <div style={styles.cardText}>
                                Upload a contract file for analysis. Supports DOCX and PDF formats.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🚀</div>
                            <div style={styles.cardTitle}>start_analysis</div>
                            <div style={styles.cardText}>
                                Begin the 8-stage contract analysis pipeline with your deal context and position.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>📊</div>
                            <div style={styles.cardTitle}>get_analysis_status</div>
                            <div style={styles.cardText}>
                                Check analysis progress and retrieve deliverables when complete.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>📋</div>
                            <div style={styles.cardTitle}>list_analyses</div>
                            <div style={styles.cardText}>
                                View all your past analyses with their status and results.
                            </div>
                        </div>
                    </div>

                    <h3 style={styles.subheading}>Example Conversations</h3>
                    <p style={styles.paragraph}>
                        With the MCP server configured, you can analyze <strong>any contract type</strong> through natural conversation:
                    </p>
                    <div style={styles.codeBlock}>
                        <code style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{`You: "Analyze this vendor agreement from Acme Corp.
      We're the customer and this is a $500K ACV deal."

Claude: [Uploads contract, starts analysis, monitors progress]

Claude: "Analysis complete. Here's what I found:
        - Contract Type: SaaS Agreement (auto-detected)
        - Favorability Score: 62/100 (vendor-favorable)
        - Top Risks: Uncapped liability, auto-renewal trap
        - Download your redlined DOCX here: [link]
        - Draft negotiation email ready for review"

---

You: "Review this commercial lease - we're the tenant."

Claude: "Analysis complete:
        - Contract Type: Commercial Lease
        - Favorability: 55/100 (landlord-favorable)
        - Key Issues: Broad default triggers, CAM pass-throughs
        - Redlined DOCX and negotiation email ready"`}</code>
                    </div>

                    <div style={styles.disclaimer}>
                        <strong>API Key Required:</strong> You need a BitsBound account and API key to use the MCP server.
                        Generate your API key from your <a href="/account" style={{ color: '#20b2aa' }}>account dashboard</a>.
                    </div>
                </>
            ),
        },
        {
            id: 'claude-code-plugin',
            title: 'Claude Code Plugin',
            icon: '⚡',
            content: (
                <>
                    <p style={styles.paragraph}>
                        The fastest way to get BitsBound in Claude Code is through the Plugin Marketplace.
                        One command installs everything — no manual configuration needed.
                    </p>
                    <div style={styles.highlight}>
                        <div style={styles.highlightTitle}>Why Use the Plugin?</div>
                        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
                            The Claude Code Plugin handles all MCP server configuration automatically.
                            Just install, add your API key, and start analyzing contracts directly from Claude Code.
                        </p>
                    </div>

                    <h3 style={styles.subheading}>Installation</h3>
                    <p style={styles.paragraph}>
                        In Claude Code, run the <code style={{ color: '#20b2aa' }}>/install-plugin</code> command:
                    </p>
                    <div style={styles.codeBlock}>
                        <code>/install-plugin BitsBound/bitsbound-mcp-server</code>
                    </div>
                    <p style={styles.paragraph}>
                        Claude Code will automatically:
                    </p>
                    <ul style={styles.list}>
                        <li style={styles.listItem}>Download the plugin from GitHub</li>
                        <li style={styles.listItem}>Configure the MCP server</li>
                        <li style={styles.listItem}>Prompt you for your API key</li>
                    </ul>

                    <h3 style={styles.subheading}>Configuration</h3>
                    <p style={styles.paragraph}>
                        After installation, add your API key to your Claude configuration at <code style={{ color: '#20b2aa' }}>~/.claude.json</code>:
                    </p>
                    <div style={styles.codeBlock}>
                        <code style={{ whiteSpace: 'pre-wrap' }}>{`{
  "mcpServers": {
    "bitsbound": {
      "command": "npx",
      "args": ["-y", "@bitsbound/mcp-server"],
      "env": {
        "BITSBOUND_API_KEY": "sk_live_your_api_key_here"
      }
    }
  }
}`}</code>
                    </div>

                    <h3 style={styles.subheading}>Using BitsBound in Claude Code</h3>
                    <p style={styles.paragraph}>
                        Once installed, you can analyze contracts with natural language:
                    </p>
                    <div style={styles.codeBlock}>
                        <code style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{`You: "Analyze the contract at /path/to/agreement.docx.
      We're the customer (debtor) in this equipment financing deal."

Claude: [Reads local file, uploads to BitsBound, starts analysis]

Claude: "Analysis complete!
        - Contract Type: Equipment Financing (Security Agreement)
        - Favorability: 58/100 (secured party favorable)
        - Key Issues: Broad default triggers, excessive remedies
        - Redlined DOCX: [download link]
        - Negotiation Email: [ready for review]"`}</code>
                    </div>

                    <h3 style={styles.subheading}>Supported Contract Types</h3>
                    <p style={styles.paragraph}>
                        BitsBound automatically detects contract type and adjusts party terminology:
                    </p>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Contract Type</th>
                                <th style={styles.th}>Party Options</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td style={styles.td}>SaaS Agreement</td><td style={styles.td}>Customer | Vendor</td></tr>
                            <tr><td style={styles.td}>M&A Transaction</td><td style={styles.td}>Buyer | Seller</td></tr>
                            <tr><td style={styles.td}>Equipment Lease</td><td style={styles.td}>Lessee | Lessor</td></tr>
                            <tr><td style={styles.td}>Equipment Financing</td><td style={styles.td}>Debtor | Secured Party</td></tr>
                            <tr><td style={styles.td}>Security Agreement</td><td style={styles.td}>Debtor | Secured Party</td></tr>
                            <tr><td style={styles.td}>Loan Agreement</td><td style={styles.td}>Borrower | Lender</td></tr>
                            <tr><td style={styles.td}>NDA</td><td style={styles.td}>Disclosing Party | Receiving Party</td></tr>
                            <tr><td style={styles.td}>Commercial Lease</td><td style={styles.td}>Tenant | Landlord</td></tr>
                            <tr><td style={styles.td}>Professional Services</td><td style={styles.td}>Client | Service Provider</td></tr>
                        </tbody>
                    </table>

                    <div style={styles.grid}>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>📂</div>
                            <div style={styles.cardTitle}>Local File Access</div>
                            <div style={styles.cardText}>
                                Claude Code can read files directly from your filesystem. Just provide the path — no manual upload needed.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>🔄</div>
                            <div style={styles.cardTitle}>Auto-Updates</div>
                            <div style={styles.cardText}>
                                Plugin updates automatically when you run <code style={{ fontSize: '0.8rem' }}>/update-plugins</code> in Claude Code.
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.cardIcon}>💬</div>
                            <div style={styles.cardTitle}>Natural Conversation</div>
                            <div style={styles.cardText}>
                                Ask questions, request changes, and iterate on redlines without leaving your Claude Code session.
                            </div>
                        </div>
                    </div>

                    <div style={styles.disclaimer}>
                        <strong>npm Package:</strong> The plugin uses <code style={{ color: '#20b2aa' }}>@bitsbound/mcp-server</code> from npm.
                        View on <a href="https://www.npmjs.com/package/@bitsbound/mcp-server" target="_blank" rel="noopener noreferrer" style={{ color: '#20b2aa' }}>npmjs.com</a>.
                    </div>
                </>
            ),
        },
    ];

    const activeContent = sections.find(s => s.id === activeSection)?.content;

    // VII. RENDER
    const currentSection = sections.find(s => s.id === activeSection);

    // T_MOBILE_RESPONSIVE_2026_01_02: Mobile-aware styles
    const mobileStyles = {
        header: {
            ...styles.header,
            padding: isMobile ? '16px 20px' : '24px 48px',
            flexWrap: 'wrap' as const,
            gap: '12px',
        },
        headerTitle: {
            ...styles.headerTitle,
            fontSize: isMobile ? '18px' : '24px',
        },
        main: {
            ...styles.main,
            padding: isMobile ? '20px 16px' : '40px',
            flexDirection: isMobile ? 'column' as const : 'row' as const,
            gap: isMobile ? '20px' : '48px',
        },
        sidebar: {
            ...styles.sidebar,
            width: isMobile ? '100%' : '300px',
            display: isMobile && !sidebarOpen ? 'none' : 'block',
        },
        contentCard: {
            ...styles.contentCard,
            padding: isMobile ? '20px' : '40px',
        },
        table: {
            ...styles.table,
            display: 'block',
            overflowX: 'auto' as const,
        },
        footer: {
            ...styles.footer,
            padding: isMobile ? '24px 16px' : '40px 48px',
        },
        footerLinks: {
            ...styles.footerLinks,
            flexWrap: 'wrap' as const,
            gap: isMobile ? '12px' : '24px',
        },
    };

    const handleSectionClick = (sectionId: string) => {
        setActiveSection(sectionId);
        if (isMobile) setSidebarOpen(false);
    };

    return (
        <div style={styles.container} className={className}>
            {/* Header */}
            <header style={mobileStyles.header}>
                <div style={styles.headerLeft}>
                    <a href="/" style={styles.logoLink}>
                        <img
                            src="https://www.bitsbound.com/favicon.svg"
                            alt="BitsBound"
                            style={styles.logo}
                        />
                    </a>
                    <div style={styles.headerTitleWrapper}>
                        <h1 style={mobileStyles.headerTitle}>Documentation</h1>
                        <p style={styles.headerSubtitle}>
                            Everything you need to know about BitsBound
                        </p>
                    </div>
                </div>
                {/* Mobile menu toggle */}
                {isMobile && (
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        style={{
                            background: 'rgba(32, 178, 170, 0.2)',
                            border: '1px solid rgba(32, 178, 170, 0.4)',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            color: '#20b2aa',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                    >
                        {sidebarOpen ? '✕ Close' : '☰ Sections'}
                    </button>
                )}
            </header>

            {/* Main Content */}
            <main style={mobileStyles.main}>
                {/* Sidebar Navigation */}
                <aside style={mobileStyles.sidebar}>
                    <div style={styles.sidebarInner}>
                        <div style={styles.sidebarHeader}>
                            <span style={styles.sidebarIcon}>📑</span>
                            <h3 style={styles.navTitle}>Sections</h3>
                        </div>
                        <div style={styles.navList}>
                            {sections.map((section) => (
                                <button
                                    key={section.id}
                                    onClick={() => handleSectionClick(section.id)}
                                    style={{
                                        ...styles.navItem,
                                        ...(activeSection === section.id ? styles.navItemActive : {}),
                                    }}
                                    onMouseOver={(e) => {
                                        if (activeSection !== section.id) {
                                            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                                            e.currentTarget.style.color = '#ffffff';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (activeSection !== section.id) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                                        }
                                    }}
                                >
                                    <span style={styles.navItemIcon}>{section.icon}</span>
                                    <span style={styles.navItemLabel}>{section.title}</span>
                                    {activeSection === section.id && (
                                        <span style={styles.navItemIndicator}>●</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </aside>

                {/* Content Area */}
                <article style={styles.content}>
                    <div style={mobileStyles.contentCard}>
                        {/* Section Header */}
                        <div style={styles.sectionHeader}>
                            <div style={styles.sectionIconWrapper}>
                                {currentSection?.icon || '📄'}
                            </div>
                            <div>
                                <h2 style={styles.sectionTitle}>{currentSection?.title}</h2>
                                <p style={styles.sectionSubtitle}>BitsBound Documentation</p>
                            </div>
                        </div>
                        {activeContent}
                    </div>
                </article>
            </main>

            {/* Footer */}
            <footer style={mobileStyles.footer}>
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <div style={mobileStyles.footerLinks}>
                        <a href="/terms-of-service" style={styles.footerLink}>Terms of Service</a>
                        <span style={styles.footerDivider}>|</span>
                        <a href="/privacy" style={styles.footerLink}>Privacy Policy</a>
                        <span style={styles.footerDivider}>|</span>
                        <a href="/security" style={styles.footerLink}>Security</a>
                        <span style={styles.footerDivider}>|</span>
                        <a href="/" style={styles.footerLink}>Home</a>
                    </div>
                    <p style={styles.footerCopyright}>
                        © 2025 BitsBound, Inc. (A Taytrom Company). All rights reserved.
                    </p>
                    <p style={styles.footerDate}>
                        Last updated: December 27, 2025
                    </p>
                </div>
            </footer>
        </div>
    );
};

// VIII. EXPORTS
export default DocumentationPea;
